import React, { useMemo, useState, useEffect } from "react";
import "devextreme/dist/css/dx.light.css";
import "./SeasonalityDashboard.css";
import Chart, {
  Series,
  CommonSeriesSettings,
  ArgumentAxis,
  ValueAxis,
  Label,
  Legend,
  Tooltip,
  Title,
  Format,
} from "devextreme-react/chart";
import DateBox from "devextreme-react/date-box";

import { baseUrl } from "../hooks/useFetch";
import useCobDate from "../hooks/useCobDate";

const FY_KEYS: string[] = ["FY22", "FY23", "FY24", "FY25", "FY26"];
const NAME_TO_KEY: Record<string, string> = {
  "FY-22": "FY22",
  "FY-23": "FY23",
  "FY-24": "FY24",
  "FY-25": "FY25",
  "FY-26": "FY26",
  "5 yr Average": "AVG",
};
const Q1 = "Q1 (Apr 1 - Jun 30)";
const Q2 = "Q2 (Jul 1 - Sep 30)";
const Q3 = "Q3 (Oct 1 - Dec 31)";
const Q4 = "Q4 (Jan 1 - Mar 31)";
const Q_LABELS_DEFAULT: Record<string, string> = {
  Q1: "Q1 (Apr 1 - Jun 30)",
  Q2: "Q2 (Jul 1 - Sep 30)",
  Q3: "Q3 (Oct 1 - Dec 31)",
  Q4: "Q4 (Jan 1 - Mar 31)",
};

interface ChartPanelProps {
  title?: string;
  data: any[] | null;
  valueFormatter: (e: { value: number; valueText?: string }) => string;
}

interface SeasonalRow {
  q: string;
  [key: string]: any;
}

interface SeasonalRawRow {
  fyyear: number;
  fiscal_quarter: string;
  pnl_quarter?: number;
  [key: string]: any;
}

interface ToSeasonalOptions {
  valueKey?: string;
  qLabels?: Record<string, string>;
  fillZeros?: boolean;
}

function ChartPanel({ title, data, valueFormatter }: ChartPanelProps): React.ReactElement {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const fyKey = (fyyear: number): string => `FY${String(fyyear).slice(-2).padStart(2, "0")}`;

  function toSeasonalQuarter(
    data1: SeasonalRawRow[] = [],
    { valueKey = "pnl_quarter", qLabels = Q_LABELS_DEFAULT, fillZeros = true }: ToSeasonalOptions = {}
  ): SeasonalRow[] {
    const fyList = Array.from(new Set(data1.map((d) => d.fyyear))).sort((a, b) => a - b);
    const fyCols = fyList.map(fyKey);
    const initRow = (qCode: string): SeasonalRow => {
      const row: SeasonalRow = { q: qLabels[qCode] ?? qCode };
      if (fillZeros) fyCols.forEach((c) => (row[c] = 0));
      return row;
    };
    const rows: Record<string, SeasonalRow> = { Q1: initRow("Q1"), Q2: initRow("Q2"), Q3: initRow("Q3"), Q4: initRow("Q4") };
    for (const r of data1) {
      const qCode = r.fiscal_quarter;
      const col = fyKey(r.fyyear);
      const val = Number(r[valueKey]) || 0;
      if (!rows[qCode]) rows[qCode] = initRow(qCode);
      if (!(col in rows[qCode])) rows[qCode][col] = 0;
      rows[qCode][col] += val;
    }
    return ["Q1", "Q2", "Q3", "Q4"].map((q) => rows[q]);
  }

  const seasonal = useMemo(() => toSeasonalQuarter(Array.isArray(data) ? data : []), [data]);

  const onLegendClick = (e: any): void => {
    const key = NAME_TO_KEY[e.target?.name];
    if (!key) return;
    e.cancel = true;
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const visibleFY = useMemo(() => FY_KEYS.filter((k) => !hidden.has(k)), [hidden]);
  const avgBasis = visibleFY.length ? visibleFY : FY_KEYS;
  const avgKey = "AVG";
  const visibleFYCount = FY_KEYS.filter(k => !hidden.has(k)).length;
  const avgVisible = !hidden.has(avgKey);

   const avgName =
   avgVisible && visibleFYCount === 0
    ? "5 yr Average"
    : `${visibleFYCount} yr Average`;

  const dataWithAvg = useMemo(() => {
    return (seasonal || []).map((r) => {
      const vals = avgBasis.map((k) => Number(r[k])).filter(Number.isFinite);
      const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
      return { ...r, Avg5: +avg.toFixed(2) };
    });
  }, [seasonal, avgBasis]);

  return (
    <Chart dataSource={dataWithAvg} onLegendClick={onLegendClick} redrawOnResize>
      <CommonSeriesSettings argumentField="q" type="bar" hoverMode="allArgumentPoints" selectionMode="allArgumentPoints">
       <Label visible={true} customizeText={(e: any) =>
         Math.abs(e.value) <= 1e-6 ? "" :
    (e.value / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })}>
          <Format type="fixedPoint" precision={1} />
        </Label>
      </CommonSeriesSettings>

      <ArgumentAxis argumentType="string" />

        <ValueAxis>
          <Label customizeText={(e: any) =>
            (e.value / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 })
          } />
        </ValueAxis>

      <Series valueField="FY22" name="FY-22" visible={!hidden.has("FY22")} />
      <Series valueField="FY23" name="FY-23" visible={!hidden.has("FY23")} />
      <Series valueField="FY24" name="FY-24" visible={!hidden.has("FY24")} />
      <Series valueField="FY25" name="FY-25" visible={!hidden.has("FY25")} />
      <Series valueField="FY26" name="FY-26" visible={!hidden.has("FY26")} />

      <Series type="bar" valueField="Avg5" name={avgName} color="#000000" width={2} visible={!hidden.has("AVG")} />

      <Legend verticalAlignment="bottom" horizontalAlignment="center" />
      <Tooltip
        enabled
        customizeTooltip={(p: any) => ({
          text:
            p.seriesName === avgName
              ? `Avg: ${valueFormatter({ value: p.value, valueText: p.valueText })}`
              : `${p.seriesName}: ${valueFormatter({ value: p.value, valueText: p.valueText })}`,
        })}
      />
      <Title text={title} />
    </Chart>
  );
}

export default function SeasonalityDashboard(): React.ReactElement {
  const [cobDate, setCobDate, cobLoading, latestCobDate] = useCobDate("/pnl_latest_cob");
  const [payload, setPayload] = useState<any>(null);
  const [marketMakerPnL, setMarketMakerPnL] = useState<any[] | null>(null);
  const [salesPnL, setSalesPnL] = useState<any[] | null>(null);
  const [rmsPnL, setRMSPnL] = useState<any[] | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        if (!cobDate) return;
        const formattedSelectedDate = cobDate.toISOString().split("T")[0];
        const res = await fetch(`${baseUrl}/pnl_seasonality?cob_date=${formattedSelectedDate}`, { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setPayload(data);
        const marketMaker_pnl = data?.["MARKET MAKER TOTAL"] || [];
        setMarketMakerPnL(Array.isArray(marketMaker_pnl) ? marketMaker_pnl : []);
         const rms_pnl = data?.["TOTAL RMS"] || [];
        setRMSPnL(Array.isArray(rms_pnl) ? rms_pnl : []);
        const sales_pnl = data?.["SALES TOTAL"] || [];
        setSalesPnL(Array.isArray(sales_pnl) ? sales_pnl : []);
      } catch (err: any) {
        if (err.name !== "AbortError") console.error("Failed to load PnL data", err);
      }
    })();
    return () => ctrl.abort();
  }, [cobDate]);

  const valueInMillions = (e: { value: number; valueText?: string }): string =>
    (Number(e.value) / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + " €mm";

  return (
    <div className="seasonality-dashboard">
      <div className="cob-section">
        <label>
          <strong>CoB:</strong>
        </label>
        <DateBox value={cobDate} onValueChanged={(e: any) => setCobDate(e.value)} width={180} displayFormat="dd/MM/yyyy" max={latestCobDate ?? undefined} />
      </div>

      <div className="charts-grid">
        <div className="chart chart--top">
          <h4>RMS P&L (€mm)</h4>
          {Array.isArray(marketMakerPnL) && (
            <ChartPanel data={rmsPnL} valueFormatter={valueInMillions} />
          )}
        </div>

        <div className="chart">
          <h4>Market Maker (€mm)</h4>
          <ChartPanel data={marketMakerPnL} valueFormatter={valueInMillions} />
        </div>

        <div className="chart">
          <h4>Sales P&L (€mm)</h4>
          <ChartPanel data={salesPnL} valueFormatter={valueInMillions} />
        </div>
      </div>
    </div>
  );
}
